
import { monitoringService } from './monitoringService';

/**
 * Wraps an API call with error and performance monitoring
 */
export const monitorApiCall = async <T>(
  promise: Promise<T>,
  operationName: string,
  context: Record<string, any> = {}
): Promise<T> => {
  const startTime = performance.now();
  
  try {
    const result = await promise;
    const duration = performance.now() - startTime;
    
    monitoringService.capturePerformance(operationName, duration, {
      ...context,
      success: true
    });
    
    return result;
  } catch (error) {
    const duration = performance.now() - startTime;
    
    if (error instanceof Error) {
      monitoringService.captureError(error, {
        ...context,
        operationName,
        duration
      });
    }
    
    throw error;
  }
};

/**
 * Track feature usage for analytics
 */
export const trackFeatureUsage = (
  featureName: string,
  properties: Record<string, any> = {}
): void => {
  monitoringService.captureMessage(`Feature used: ${featureName}`, 'info', {
    ...properties,
    feature: featureName,
    type: 'feature_usage'
  });
};

/**
 * Log user actions for analytics and monitoring
 */
export const logUserAction = (
  action: string,
  properties: Record<string, any> = {}
): void => {
  monitoringService.captureMessage(`User action: ${action}`, 'info', {
    ...properties,
    action,
    type: 'user_action'
  });
};

/**
 * Run a health check on the application
 */
export const checkAppHealth = (): Record<string, any> => {
  const health = {
    status: 'healthy',
    timestamp: new Date().toISOString(),
    features: {
      api: true,
      storage: true,
      monitoring: monitoringService['isInitialized'] || false
    }
  };
  
  return health;
};

/**
 * Safe error handling for asynchronous operations
 */
export const tryCatch = async <T>(
  fn: () => Promise<T>,
  operationName: string,
  fallbackValue?: T
): Promise<T | undefined> => {
  try {
    return await fn();
  } catch (error) {
    if (error instanceof Error) {
      monitoringService.captureError(error, {
        operationName,
        timestamp: new Date().toISOString()
      });
    }
    
    return fallbackValue;
  }
};

/**
 * Safe error handling for synchronous operations
 */
export const tryCatchSync = <T>(
  fn: () => T,
  operationName: string,
  fallbackValue?: T
): T | undefined => {
  try {
    return fn();
  } catch (error) {
    if (error instanceof Error) {
      monitoringService.captureError(error, {
        operationName,
        timestamp: new Date().toISOString()
      });
    }
    
    return fallbackValue;
  }
};

/**
 * Warn about deprecated functionality usage
 */
export const warnDeprecatedUsage = (
  oldFeature: string,
  newFeature: string
): void => {
  const message = `Deprecated feature used: ${oldFeature}. Please use ${newFeature} instead.`;
  monitoringService.captureMessage(message, 'warn', {
    oldFeature,
    newFeature,
    type: 'deprecation_warning'
  });
  
  console.warn(message);
};
